import { Badge, ScrollArea, Skeleton } from '@mantine/core';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import type { Endpoints } from '@xver/apis';

type AnnouncementItem = Endpoints.v1.Announcements.Model;

interface AnnouncementBoxProps {
    isLoading?: boolean;
    announcements?: AnnouncementItem[];
}

const TONE_COLOR: Record<AnnouncementItem['tone'], string> = {
    info: 'var(--color-status-info)',
    success: 'var(--color-status-teal)',
    critical: 'var(--color-status-danger)',
};

const ANNOUNCEMENT_SKELETON_ITEMS = Array.from({ length: 2 });

const AnnouncementSkeleton = memo(function AnnouncementSkeleton() {
    return (
        <article className="w-full min-h-48 flex flex-col justify-start items-start gap-3 border-[0.5px] bg-surface-2 border-line rounded-md p-4">
            <div className="w-full flex items-center justify-between gap-3">
                <Skeleton height={24} width={96} radius="sm" />
                <Skeleton height={14} width={64} />
            </div>
            <Skeleton height={20} width="80%" />
            <Skeleton height={16} width="100%" />
            <Skeleton height={16} width="92%" />
            <Skeleton height={16} width="70%" />
        </article>
    );
});

function AnnouncementCard({ announcement }: { announcement: AnnouncementItem }) {
    const { t } = useTranslation('dashboard');
    const color = TONE_COLOR[announcement.tone] ?? TONE_COLOR.info;

    return (
        <article className="w-full min-h-48 flex flex-col justify-start items-start gap-3 border-[0.5px] bg-surface-2 border-line rounded-md p-4">
            <div className="w-full flex items-center justify-between gap-3">
                <Badge
                    variant="light"
                    color={color}
                    size="md"
                    radius="sm"
                    className="inline-flex items-center"
                >
                    <span>{t('announcement.badge')}</span>
                </Badge>
                <span className="text-xs text-fg/68">{announcement.tag}</span>
            </div>
            <h3 className="text-lg font-semibold leading-snug line-clamp-3">{announcement.name}</h3>
            <p className="text-sm text-fg/70 line-clamp-4 whitespace-pre-wrap">
                {announcement.text}
            </p>
        </article>
    );
}

function AnnouncementBox({ isLoading = false, announcements = [] }: AnnouncementBoxProps) {
    const { t } = useTranslation('dashboard');

    return (
        <div className="flex h-full flex-col justify-start gap-1 overflow-hidden">
            <div className="flex items-center gap-2">
                <h2 className="text-xl">{t('announcement.title')}</h2>
            </div>
            <p className="text-sm text-fg/68">{t('announcement.subtitle')}</p>
            <ScrollArea
                className="mt-3.5 w-full h-124"
                offsetScrollbars
                overscrollBehavior="contain"
                scrollbars="y"
            >
                {isLoading ? (
                    <div className="w-full grid grid-cols-1 gap-4 pr-1">
                        {ANNOUNCEMENT_SKELETON_ITEMS.map((_, index) => (
                            <AnnouncementSkeleton key={index} />
                        ))}
                    </div>
                ) : announcements.length === 0 ? (
                    <div className="flex h-full min-h-48 items-center justify-center text-sm text-fg/68">
                        {t('announcement.noAnnouncement')}
                    </div>
                ) : (
                    <div className="w-full grid grid-cols-1 gap-4 pr-1">
                        {announcements.map((announcement) => (
                            <AnnouncementCard key={announcement.id} announcement={announcement} />
                        ))}
                    </div>
                )}
            </ScrollArea>
        </div>
    );
}

export default memo(AnnouncementBox);
